import { LitElement, html, nothing } from 'lit';
import { LocaleController } from '../i18n/LocaleController';
import { t } from '../i18n';
import { pillTabBar } from './templates';
import '../elements/vitesse-icon';
/*
 * BasePage — the ONE page primitive, app-wide.
 *
 * A 1:1 Lit port of the agent's BasePage chrome (back button · title [+ entity
 * icon] · description · the badges/actions row · a FULL-HEIGHT content slot), as
 * an INHERITED base class in the exact mold of BaseSection: the abstract base
 * owns render() and the `.base-page-*` scaffold; a concrete page
 * `extends BasePage` and fills the hooks — required renderContent()/pageTitle()/
 * onBack()/backLabel(), optional titleIcon()/renderDescription()/renderBadges()/
 * renderActions()/backAriaLabel(). Light DOM, so the agent's themeable
 * base-page.scss styles the emitted markup verbatim — no per-page chrome, no
 * invented classes, light/dark for free.
 *
 * Canonical structure (matches agent/src/components/common/BasePage.tsx):
 *   .base-page > .base-page-main-header
 *     .base-page-back-button
 *     .base-page-header > .base-page-title-section > h1.base-page-title [+ icon]
 *     .base-page-description                       ← SIBLING after the header
 *     .base-page-status-actions-row                ← only when badges|actions
 *       .base-page-status-actions-left  (badges)
 *       .base-page-status-actions-right (actions)
 *   .base-page-content                              ← flex:1, reaches the bottom
 *
 * The welcome/home page is the deliberate exception — it's a hero landing, not a
 * back-button detail page — so it does NOT extend BasePage.
 */
export class BasePage extends LitElement {
    constructor() {
        super();
        this.stickyRaf = 0;
        /** Whether the last render was compact — see compactHeader(). */
        this.compactShown = false;
        this.onScroll = () => {
            if (this.stickyRaf)
                return; // rAF-throttle: at most one update per frame
            this.stickyRaf = requestAnimationFrame(() => {
                this.stickyRaf = 0;
                this.updateSticky();
            });
        };
        new LocaleController(this); // every page re-renders on locale change
        // A page that LEAVES compact mode gets its main header back wherever the column is scrolled,
        // and the bar must carry its pieces for that spot. A controller rather than updated(),
        // because pages override updated() without calling super.
        this.addController({
            hostUpdated: () => {
                const compact = this.compactHeader();
                if (compact === this.compactShown)
                    return;
                this.compactShown = compact;
                this.updateSticky();
            },
        });
    }
    createRenderRoot() {
        return this; // light DOM → global token-based SCSS applies
    }
    /** The compact bar's height — also the carry line offset below the scroll top. */
    static { this.STICKY_H = 52; }
    // [carry key on the sticky piece, selector of its main-header twin to track]
    static { this.CARRY = [
        ['back', '.base-page-back-button'],
        ['title', '.base-page-title'],
        ['badges', '.base-page-status-actions-row'],
    ]; }
    firstUpdated() {
        this.scrollParent = this.scrollContainer();
        this.scrollParent?.addEventListener('scroll', this.onScroll, { passive: true });
        this.updateSticky(); // initial state (covers a restored scroll position)
    }
    /** The element whose scroll drives the sticky header. The shell's main column by default; a
     *  page mounted elsewhere (the marketplace inside a rail) overrides this with its own scroller. */
    scrollContainer() {
        return this.findScrollParent();
    }
    /** The scrolling ancestor (`.main-content`) — found by walking the COMPOSED ancestor chain
     *  (slots + shadow hosts), NOT closest(). The app shell (<vitesse-shell>) slots pages into a
     *  `.main-content` that lives in its SHADOW DOM, which closest() can't reach; this walk
     *  crosses the boundary via assignedSlot/host. In a light-DOM shell it still finds the same
     *  `.main-content` via parentNode, so behaviour is unchanged there. */
    findScrollParent() {
        let node = this.assignedSlot ?? this.parentNode;
        while (node) {
            if (node instanceof HTMLElement && node.classList.contains('main-content'))
                return node;
            if (node instanceof ShadowRoot)
                node = node.host;
            else if (node instanceof Element)
                node = node.assignedSlot ?? node.parentNode;
            else
                node = node.parentNode;
        }
        return null;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.scrollParent?.removeEventListener('scroll', this.onScroll);
        if (this.stickyRaf)
            cancelAnimationFrame(this.stickyRaf);
    }
    /** Carry each sticky piece in as the bar rolls over its main-header twin; the
     *  bar slides in once the (first) back link is carried. */
    updateSticky() {
        const sp = this.scrollParent;
        if (!sp || this.compactHeader())
            return; // compact: the bar is always up (base-page.scss)
        const scrolled = sp.scrollTop > 0;
        // The carry line is the BAR'S BOTTOM edge (container top + bar height), not the
        // container's very top. So a piece slides INTO the bar the instant the bar
        // starts rolling over it — there's never a frame where it has slipped behind the
        // translucent bar but not yet reappeared in it. (Gated on an actual scroll so
        // the back link — which starts within the bar's band — isn't carried at rest.)
        const lineY = sp.getBoundingClientRect().top + BasePage.STICKY_H;
        // A SHORT page can run out of scroll before a lower piece ever reaches the line — a
        // marketplace of eight cards in a wide rail stops with its badges a few pixels shy of the
        // bar, and the bar sits there without them. Once the bar is up and the scroller is at its
        // end, every remaining piece is carried too: nothing further down can ever bring it.
        const atEnd = sp.scrollTop + sp.clientHeight >= sp.scrollHeight - 1;
        let barVisible = false;
        for (const [key, sel] of BasePage.CARRY) {
            const main = this.querySelector(sel);
            const piece = this.querySelector(`[data-sticky="${key}"]`);
            if (!main || !piece)
                continue;
            const carried = scrolled && (main.getBoundingClientRect().bottom <= lineY || (atEnd && barVisible));
            piece.classList.toggle('base-page-sticky-carried', carried);
            if (key === 'back')
                barVisible = carried; // back is the first/last to carry
        }
        this.querySelector('.base-page-sticky-header')?.classList.toggle('base-page-sticky-visible', barVisible);
    }
    scrollToTop() {
        this.scrollParent?.scrollTo({ top: 0, behavior: 'smooth' });
    }
    /** The compact sticky bar — back · title · badges (left-aligned), each carried
     *  in independently, + the scroll-to-top nub on the bottom edge. A compact page
     *  has no main header to hold its actions, so the bar holds them, on the right. */
    renderStickyHeader(compact, actions) {
        const icon = this.titleIcon();
        const badges = this.renderBadges();
        return html `
      <div class="base-page-sticky-header">
        <div class="base-page-sticky-row">
          <button
            class="base-page-sticky-back-button"
            data-sticky="back"
            @click=${() => this.onBack()}
            aria-label=${this.backAriaLabel()}
          >
            <vitesse-icon name="chevron-left" size="18"></vitesse-icon>
          </button>
          <h2 class="base-page-sticky-title" data-sticky="title">
            ${icon ? html `<vitesse-icon name=${icon} size="16"></vitesse-icon>` : nothing}${this.pageTitle()}
          </h2>
          ${badges === nothing
            ? nothing
            : html `<div class="base-page-sticky-badges" data-sticky="badges">${badges}</div>`}
          ${compact && actions !== nothing ? html `<div class="base-page-sticky-actions">${actions}</div>` : nothing}
        </div>
        <button
          class="base-page-scroll-top"
          @click=${() => this.scrollToTop()}
          title=${t('common.backToTop')}
          aria-label=${t('common.backToTop')}
        >
          <vitesse-icon name="chevron-up" size="15"></vitesse-icon>
        </button>
      </div>
    `;
    }
    // ── optional hooks (sensible no-op defaults) ───────────────────────────────
    /** vitesse-icon name shown next to the title; null → no icon. */
    titleIcon() {
        return null;
    }
    /** Inner description content; BasePage wraps it in `.base-page-description`.
     *  Return `nothing` (the default) to omit the block entirely. */
    renderDescription() {
        return nothing;
    }
    /** Status badges — rendered left of the actions row. */
    renderBadges() {
        return nothing;
    }
    /** Action buttons — rendered right of the badges in the same row. */
    renderActions() {
        return nothing;
    }
    /** Back-button aria-label; defaults to the visible label. */
    backAriaLabel() {
        return this.backLabel();
    }
    /** True → the page shows only the compact bar: no main header, no description, no AI hints,
     *  and the bar is up from the start rather than carried in by scrolling. For a page that wants
     *  its content to have the screen — a dashboard in kiosk on a wall display. A short page has
     *  nothing to scroll, so scrolling cannot be how a page gets there. */
    compactHeader() {
        return false;
    }
    /** Contextual AI prompt chips shown between the header and the content
     *  (same chips + styling as the welcome page). Default none; each page
     *  returns its own, e.g. ['What state is X in?', "What is this model's logic?"]. */
    aiHints() {
        return [];
    }
    // ── optional pill-tabs ──────────────────────────────────────────────────────
    // A detail page with sub-views (controller → Symbols/Properties/Program,
    // gateway → Controllers/Models/…) returns its tab list here; BasePage then
    // renders the ONE pill-tab bar at the top of the content area, and the page's
    // renderContent() switches on activeTabId(). Pages with no tabs (the default
    // empty list) are unchanged — no bar is rendered. This is the single shared
    // tab treatment so every tabbed detail page looks + behaves identically.
    /** The tabs to show; empty (default) → no tab bar. */
    tabs() {
        return [];
    }
    /** The currently-active tab id (the page tracks this as @state). */
    activeTabId() {
        return '';
    }
    /** A tab was clicked — the page updates its active-tab @state. */
    onTabChange(_id) { }
    /** A small control right after the tabs, in the same row: a status and its button that belong to
     *  the whole page, not to one tab. Nothing (default) → no control. */
    renderTabTrailing() {
        return nothing;
    }
    // Clicking a hint asks the AI that question — fired up to the shell, which
    // opens the AI rail (and, later, pre-fills the prompt).
    onHint(prompt) {
        this.dispatchEvent(new CustomEvent('vitesse-ai-prompt', { detail: { prompt }, bubbles: true, composed: true }));
    }
    renderHints() {
        const hints = this.aiHints();
        if (hints.length === 0)
            return nothing;
        return html `<div class="base-page-prompts">
      <div class="prompt-chips">
        ${hints.map((h) => html `<button class="prompt-chip" @click=${() => this.onHint(h)}>
            <vitesse-icon name="sparkles" size="12" class="prompt-chip-sparkle"></vitesse-icon>
            <span class="prompt-chip-text">${h}</span>
          </button>`)}
      </div>
    </div>`;
    }
    render() {
        const icon = this.titleIcon();
        const desc = this.renderDescription();
        const badges = this.renderBadges();
        const actions = this.renderActions();
        const hasRow = badges !== nothing || actions !== nothing;
        const compact = this.compactHeader();
        return html `
      <div class="base-page${compact ? ' base-page-compact' : ''}">
        ${this.renderStickyHeader(compact, actions)}
        ${compact ? nothing : this.renderMainHeader(icon, desc, badges, actions, hasRow)}
        ${compact ? nothing : this.renderHints()}

        <div class="base-page-content">
          ${this.renderTabBar()}${this.renderContent()}
        </div>
      </div>
    `;
    }
    renderMainHeader(icon, desc, badges, actions, hasRow) {
        return html `
      <div class="base-page-main-header">
        <button class="base-page-back-button" @click=${() => this.onBack()} aria-label=${this.backAriaLabel()}>
          <vitesse-icon name="chevron-left" size="18"></vitesse-icon>
          <span>${this.backLabel()}</span>
        </button>

        <div class="base-page-header">
          <div class="base-page-title-section">
            <h1 class="base-page-title">
              ${icon
            ? html `<span class="base-page-title-icon"><vitesse-icon name=${icon} size="20"></vitesse-icon></span>`
            : nothing}
              ${this.pageTitle()}
            </h1>
          </div>
        </div>

        ${desc === nothing ? nothing : html `<div class="base-page-description">${desc}</div>`}

        ${hasRow
            ? html `<div class="base-page-status-actions-row">
              ${badges === nothing ? nothing : html `<div class="base-page-status-actions-left">${badges}</div>`}
              ${actions === nothing ? nothing : html `<div class="base-page-status-actions-right">${actions}</div>`}
            </div>`
            : nothing}
      </div>
    `;
    }
    /** The shared pill-tab bar — rendered only when tabs() is non-empty, at the
     *  top of the content area (the `.entity-tab-header` slot every tabbed detail
     *  page used inline before this was promoted here). */
    renderTabBar() {
        const tabs = this.tabs();
        if (tabs.length === 0)
            return nothing;
        const trailing = this.renderTabTrailing();
        return html `<div class="entity-tab-header">
      ${pillTabBar({ tabs, active: this.activeTabId(), onChange: (id) => this.onTabChange(id) })}
      ${trailing === nothing ? nothing : html `<div class="entity-tab-trailing">${trailing}</div>`}
    </div>`;
    }
}
//# sourceMappingURL=base-page.js.map